import { useQuery } from '@tanstack/react-query';
import { Check, Download, Map, Trophy, User } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { MEDIA_URL } from '@/config';
import { Matchdata } from '@/types/match';
import { formatMapName } from '@/utils/format';

import { fetchDemo } from '../api/getDemo';
import { fetchMvp } from '../api/getMvp';
import { generateDemoDownloadLink } from '../utils';

type Props = {
  match: Matchdata;
  mapNumber: number;
};

const MapAndMvp = (props: Props) => {
  const { data: demo } = useQuery({
    queryKey: ['demo', props.match.id],
    queryFn: () => fetchDemo(props.match.id.toString()),
  });
  const { data: mvp } = useQuery({
    queryKey: ['mvp', props.match.id],
    queryFn: () => fetchMvp(props.match.id.toString()),
  });

  return (
    <div className="bg-block flex gap-x-6 rounded-lg border border-border px-8 py-12">
      <div className="relative flex basis-1/4 items-center gap-x-6">
        {props.match.playoffs ? (
          <>
            <div className="relative h-32 w-56 overflow-hidden rounded-lg bg-background text-2xl font-semibold">
              <img
                src={`/images/${
                  props.match.get5StatsMatch.get5StatsMaps[props.mapNumber]
                    .mapname
                }.jpg`}
                className="h-full object-cover"
                alt=""
              />

              <div className="absolute inset-0 z-10 flex items-center justify-center text-5xl font-semibold">
                <span className="mr-1">
                  {
                    props.match.get5StatsMatch.get5StatsMaps[props.mapNumber]
                      .team1_score
                  }
                </span>
                <span className="mb-2">:</span>
                <span className="ml-1">
                  {
                    props.match.get5StatsMatch.get5StatsMaps[props.mapNumber]
                      .team2_score
                  }
                </span>
              </div>
              <div className="absolute inset-0 bg-[#102453] opacity-60"></div>
            </div>
            <div className="flex w-48 flex-col gap-y-0.5 whitespace-nowrap text-lg font-semibold">
              <span className="flex gap-x-1 text-xs font-medium text-muted-foreground">
                <Trophy />
                Map winner
              </span>
              {props.match.get5StatsMatch.get5StatsMaps[props.mapNumber]
                .winner == 'team1' && props.match.team1.team_name}
              {props.match.get5StatsMatch.get5StatsMaps[props.mapNumber]
                .winner == 'team2' && props.match.team2.team_name}
              <a
                href={
                  demo &&
                  generateDemoDownloadLink(demo, props.match, props.mapNumber)
                }
                target="_blank"
                className="absolute bottom-0 w-full"
                rel="noreferrer"
              >
                <Button
                  variant="secondary"
                  className="mt-1 whitespace-nowrap"
                  disabled={!demo}
                >
                  <Download />
                  Download demo
                </Button>
              </a>
            </div>
          </>
        ) : (
          <>
            <div className="h-32 w-32 overflow-hidden rounded-lg bg-background text-2xl font-semibold">
              <img
                src={`/images/${
                  props.match.get5StatsMatch.get5StatsMaps[props.mapNumber]
                    .mapname
                }.jpg`}
                className="h-full object-cover"
                alt=""
              />
            </div>
            <div className="flex w-48 flex-col gap-y-0.5 text-lg font-semibold">
              <span className="flex gap-x-1 text-xs font-medium text-muted-foreground">
                <Map />
                Map
              </span>
              {formatMapName(
                props.match.get5StatsMatch.get5StatsMaps[props.mapNumber]
                  .mapname || '',
              )}
              <a
                href={
                  demo &&
                  generateDemoDownloadLink(demo, props.match, props.mapNumber)
                }
                target="_blank"
                className="absolute bottom-0 w-full"
                rel="noreferrer"
              >
                <Button
                  variant="secondary"
                  className="mt-1 whitespace-nowrap"
                  disabled={!demo}
                >
                  <Download />
                  Download demo
                </Button>
              </a>
            </div>
          </>
        )}
      </div>
      <div className="flex basis-1/4 items-center gap-x-6">
        <Avatar className="h-32 w-32 rounded-lg">
          <AvatarImage
            src={`${
              mvp?.user.profile_photo.includes('https')
                ? mvp?.user.profile_photo
                : `${MEDIA_URL}/${mvp?.user.profile_photo}`
            }`}
            alt={mvp?.user.username}
            className="rounded-lg"
          />
          <AvatarFallback className="rounded-lg bg-secondary">
            <User />
          </AvatarFallback>
        </Avatar>
        <div className="flex flex-col gap-y-0.5 text-lg font-semibold">
          <span className="flex gap-x-1 whitespace-nowrap text-xs font-medium text-muted-foreground">
            <Trophy />
            Player of the game
          </span>
          {mvp?.user.username}
        </div>
      </div>
      <div className="ml-auto flex basis-2/4 items-center justify-between">
        <div className="mr-12 flex items-center gap-x-4">
          <div className="bg-secondary-light grid h-16 w-16 place-items-center overflow-hidden rounded-md">
            <Check className="text-black" />
          </div>
          <div className="flex flex-col gap-y-0.5 text-lg font-semibold">
            <span className="flex gap-x-1 text-xs font-medium text-muted-foreground">
              KDA
            </span>
            {mvp?.killDeathRatio}
          </div>
        </div>
        <div className="mr-12 flex items-center gap-x-4">
          <div className="bg-secondary-light grid h-16 w-16 place-items-center overflow-hidden rounded-md">
            <Check className="text-black" />
          </div>
          <div className="flex flex-col gap-y-0.5 text-lg font-semibold">
            <span className="flex gap-x-1 text-xs font-medium text-muted-foreground">
              UTIL DMG
            </span>
            {mvp?.utilityDamage}
          </div>
        </div>
        <div className="mr-12 flex items-center gap-x-4">
          <div className="bg-secondary-light grid h-16 w-16 place-items-center overflow-hidden rounded-md">
            <Check className="text-black" />
          </div>
          <div className="flex flex-col gap-y-0.5 text-lg font-semibold">
            <span className="flex gap-x-1 text-xs font-medium text-muted-foreground">
              DUEL WINRATE
            </span>
            {mvp?.entrySuccessRate?.toFixed(0)}%
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapAndMvp;
